import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createTheme, getAllThemesData, getThemeById, updateTheme, duplicateTheme } from './themeApi';
import { ActivityThemeStyles } from '../common/themes/theme';

type Modifier = {
  id: string;
  name: string;
};

export type Theme = {
  _id: string;
  name: string;
  styles: ActivityThemeStyles;
  css: string;
  updatedAt: string;
  modifiedBy: Modifier;
};

/**
 * What the write endpoints and the picker return: a theme WITHOUT its `css`. The controllers project
 * those five fields explicitly, so this is the real shape, not a convenience narrowing.
 */
export type ThemeSummary = Omit<Theme, 'css'>;

export type ThemesData = ThemeSummary[];

export type CreateThemeDto = Pick<Theme, Exclude<keyof Theme, 'updatedAt' | 'modifiedBy' | '_id'>>;

export const useFetchAllThemesData = () => {
  return useQuery<ThemesData>({
    queryKey: ['themes', 'list'],
    queryFn: () => getAllThemesData()
  });
};

/**
 * TODO refactor consumers to use useGetThemeQuery
 */
export const useTheme = (themeId?: string | null, enabled = true) => {
  return useQuery<Theme>({
    queryKey: ['theme', { themeId }],
    queryFn: () => getThemeById(themeId as string),
    enabled: !!themeId && enabled,
    // Keep the previously-loaded theme as placeholder while a new themeId is being fetched.
    // This prevents the loading gate in InitializeCssVars from unmounting the subtree (and
    // closing UI like the theme panel) during a theme switch. Skip when the next key has
    // no themeId — the query is disabled, so the placeholder would never be replaced and
    // consumers would render a stale theme instead of falling back to defaults.
    placeholderData: themeId ? keepPreviousData : undefined
  });
};

type UseUpdateThemeArgs = {
  onSuccess: () => void;
  onError: () => void;
};

export function useUpdateTheme(props: UseUpdateThemeArgs) {
  const { onSuccess, onError } = props;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTheme,
    onMutate: async updatedTheme => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: ['themes', 'list']
      });
      await queryClient.cancelQueries({
        queryKey: ['theme', { themeId: updatedTheme._id }]
      });

      const previousList = queryClient.getQueryData<ThemesData>(['themes', 'list']);
      const previousTheme = queryClient.getQueryData<Theme>(['theme', { themeId: updatedTheme._id }]);

      queryClient.setQueryData<ThemesData>(['themes', 'list'], oldList => {
        if (!oldList) return [];
        return oldList.map(theme =>
          theme._id === updatedTheme._id
            ? {
                ...theme,
                name: updatedTheme.name,
                styles: updatedTheme.styles,
                updatedAt: new Date().toISOString()
              }
            : theme
        );
      });

      queryClient.setQueryData<Theme>(['theme', { themeId: updatedTheme._id }], oldTheme => ({
        ...(oldTheme as Theme),
        ...updatedTheme,
        updatedAt: new Date().toISOString()
      }));

      // Return for rollback
      return { previousList, previousTheme };
    },
    onError: (err, updatedTheme, context) => {
      // Rollback on error
      if (context?.previousList) {
        queryClient.setQueryData(['themes', 'list'], context.previousList);
      }
      if (context?.previousTheme) {
        queryClient.setQueryData(['theme', { themeId: updatedTheme._id }], context.previousTheme);
      }
      onError();
    },
    onSuccess: () => {
      onSuccess();
    },
    onSettled: async (_data, _error, { _id }) => {
      // Refetch to ensure server state
      await queryClient.invalidateQueries({
        queryKey: ['themes', 'list']
      });
      await queryClient.invalidateQueries({
        queryKey: ['theme', { themeId: _id }]
      });
    }
  });
}

export function useCreateTheme({ onSuccess, onError }: { onSuccess: (themeId: string) => void; onError: () => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (createThemeDto: CreateThemeDto) => createTheme(createThemeDto),
    onSuccess: ({ _id, name, updatedAt, styles, modifiedBy }) => {
      void queryClient.setQueryData<ThemesData>(['themes', 'list'], oldList => [
        { _id, name, updatedAt, styles, modifiedBy },
        ...(oldList ?? [])
      ]);
      onSuccess(_id);
    },
    onError
  });
}

export function useDuplicateTheme(
  id: string,
  { onSuccess, onError }: { onSuccess?: (themeId: string) => void; onError?: () => void }
) {
  const queryClient = useQueryClient();

  return useMutation<ThemeSummary>({
    mutationFn: async () => {
      await queryClient.cancelQueries({ queryKey: ['themes', 'list'] });
      // These two throw rather than return early. Resolving `undefined` made the mutation SUCCEED with no
      // theme, and `onSuccess` then destructured `_id` off it and threw inside React Query — where
      // `onError` can no longer run, so the user saw nothing at all. Throwing routes both cases to the
      // existing duplication-error notification. The typing hid this until the return type stopped being
      // `any`.
      const currentList = queryClient.getQueryData<ThemesData>(['themes', 'list']);
      if (!currentList) {
        throw new Error('Cannot duplicate a theme before the theme list has loaded');
      }
      const currentItem = currentList.find(item => item._id === id);
      if (!currentItem) {
        throw new Error(`Cannot duplicate theme ${id}: it is not in the loaded list`);
      }
      let newName = `Copy of ${currentItem.name}`;
      let index = 1;
      while (currentList.some(item => item.name === newName)) {
        newName = `Copy of ${currentItem.name} ${index}`;
        index++;
      }
      queryClient.setQueryData<ThemesData>(['themes', 'list'], oldList => {
        if (!oldList) return [];
        const currentIsoDate: string = new Date().toISOString();
        return [
          {
            _id: '',
            name: newName,
            updatedAt: currentIsoDate,
            styles: currentItem.styles,
            modifiedBy: { name: '', id: '' }
          },
          ...oldList
        ];
      });
      return duplicateTheme(id, newName);
    },
    onSuccess: ({ _id, name, updatedAt, styles, modifiedBy }) => {
      void queryClient.setQueryData<ThemesData>(['themes', 'list'], oldList => {
        if (!oldList) return [];
        return oldList.map(item => (item.name === name ? { _id, name, updatedAt, styles, modifiedBy } : item));
      });
      if (onSuccess) {
        onSuccess(_id);
      }
    },
    onError,
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['themes', 'list'] });
    }
  });
}
